/**
 * Trilingual UI strings for the enterprise-style community dashboard.
 */

export type CommunityLocale = "en-US" | "ja-JP" | "ko-KR";

export interface CommunityUiStrings {
  navLabel: string;
  pageTitle: string;
  windowDays: string;
  windowDropdownLabel: string;
  tabDashboard: string;
  tabPeople: string;
  tabAction: string;
  peopleFilterLabel: string;
  peopleFilterEveryone: string;
  peopleFilterUsers: string;
  peopleFilterParteas: string;
  userSubfilterLabel: string;
  userSubfilterAll: string;
  userSubfilterFollowing: string;
  userSubfilterFollowers: string;
  parteaSubfilterLabel: string;
  parteaSubfilterAll: string;
  parteaSubfilterMy: string;
  followingEmptyTitle: string;
  followingEmptySubtitle: string;
  followersEmptyTitle: string;
  followersEmptySubtitle: string;
  loading: string;
  sectionError: string;
  retry: string;
  openIssue: string;
  openProfile: string;
  viewIssues: string;
  viewFeedback: string;
  viewTeabates: string;
  viewSentiment: string;
  deltaUp: string;
  deltaDown: string;
  deltaFlat: string;
  deltaFromPreviousWindow: string;
  deltaPoints: string;
  enterprisePulseTitle: string;
  enterprisePulseSubtitle: string;
  kpiActiveIssues: string;
  kpiActiveContributors: string;
  kpiTotalEngagements: string;
  kpiPipelineHealth: string;
  kpiActiveTeabates: string;
  kpiGovernanceScore: string;
  changePipelineTitle: string;
  changePipelineSubtitle: string;
  pipelineStageIssues: string;
  pipelineStageAnalysis: string;
  pipelineStageStrategy: string;
  pipelineStageExecution: string;
  pipelineStageCovered: string;
  pipelineOfTotal: string;
  pipelineConversion: string;
  activityTrendTitle: string;
  activityTrendSubtitle: string;
  activityLayerContributions: string;
  activityLayerAnalysis: string;
  activityLayerGovernance: string;
  activityLayerTotal: string;
  priorityFocusTitle: string;
  priorityFocusSubtitle: string;
  priorityParetoSummary: string;
  priorityCoverageComplete: string;
  priorityMissingSolution: string;
  priorityMissingAction: string;
  priorityMissingBoth: string;
  governanceTitle: string;
  governanceSubtitle: string;
  governanceScoreLabel: string;
  feedbackPipelineTitle: string;
  feedbackPipelineSubtitle: string;
  feedbackOpen: string;
  feedbackNewInWindow: string;
  feedbackResponseRate: string;
  feedbackStatusLabels: Record<string, string>;
  teabateParticipationTitle: string;
  teabateParticipationSubtitle: string;
  teabateActive: string;
  teabateNew: string;
  teabateAlignmentParticipants: string;
  communitySentimentTitle: string;
  communitySentimentSubtitle: string;
  sentimentParticipants: string;
  sentimentResponses: string;
  sentimentPositive: string;
  sentimentNeutral: string;
  sentimentNegative: string;
  contentHealthTitle: string;
  contentHealthSubtitle: string;
  reportsInWindow: string;
  reportsPending: string;
  reportsResolved: string;
  reportResolutionRate: string;
  alignmentTitle: string;
  alignmentSubtitle: string;
  alignmentSummary: string;
  alignmentDimensionNone: string;
  alignmentDimensionLabels: Record<string, string>;
  alignmentResponses: string;
  alignmentOverallScore: string;
  alignmentDimensionParticipation: string;
  radarCaption: string;
  contributorsTitle: string;
  contributorsSubtitle: string;
  contributorComments: string;
  contributorPosts: string;
  contributorAdBreaks: string;
  contributorAwards: string;
  contributorScrollPrevious: string;
  contributorScrollNext: string;
  globalPresenceTitle: string;
  globalPresenceSubtitle: string;
  globalPublicCountryUsers: string;
  demographicsNote: string;
  countryUnknown: string;
  openOpportunitiesTitle: string;
  openOpportunitiesSubtitle: string;
  opportunityStrategyTitle: string;
  opportunityGovernanceTitle: string;
  opportunityProposeSolution: string;
  opportunityStartAction: string;
  opportunityReviewFeedback: string;
  opportunityJoinTeabates: string;
  opportunityRecordSentiment: string;
  opportunityMissingSolution: string;
  opportunityMissingAction: string;
  opportunityMissingBoth: string;
  opportunityFeedbackQueue: string;
  opportunityTeabateParticipation: string;
  opportunitySentimentCoverage: string;
  opportunityNoStrategyGaps: string;
  opportunityNoGovernanceGaps: string;
  emptyActivityTrend: string;
  emptyPriorityFocus: string;
  emptyContributors: string;
  emptyAlignment: string;
  emptyDemographics: string;
  emptyGovernance: string;
}

const STRINGS: Record<CommunityLocale, CommunityUiStrings> = {
  "en-US": {
    navLabel: "Dashboard",
    pageTitle: "Community dashboard",
    windowDays: "Last {days} days",
    windowDropdownLabel: "Time window",
    tabDashboard: "Dashboard",
    tabPeople: "People",
    tabAction: "Action",
    peopleFilterLabel: "Filter people",
    peopleFilterEveryone: "Everyone",
    peopleFilterUsers: "Users",
    peopleFilterParteas: "Parteas",
    userSubfilterLabel: "Filter users",
    userSubfilterAll: "All",
    userSubfilterFollowing: "Following",
    userSubfilterFollowers: "Followers",
    parteaSubfilterLabel: "Filter parteas",
    parteaSubfilterAll: "All",
    parteaSubfilterMy: "My parteas",
    followingEmptyTitle: "Not following anyone yet",
    followingEmptySubtitle: "Users you follow will show up here.",
    followersEmptyTitle: "No followers yet",
    followersEmptySubtitle: "When other users follow you, they'll show up here.",
    loading: "Loading community dashboard…",
    sectionError: "This section could not be loaded.",
    retry: "Retry",
    openIssue: "Open issue",
    openProfile: "Open profile",
    viewIssues: "Browse issues",
    viewFeedback: "View feedback",
    viewTeabates: "View teabates",
    viewSentiment: "Explore issue sentiment",
    deltaUp: "Up",
    deltaDown: "Down",
    deltaFlat: "Flat",
    deltaFromPreviousWindow: "vs previous window",
    deltaPoints: "pts",
    enterprisePulseTitle: "Enterprise pulse",
    enterprisePulseSubtitle:
      "Executive KPIs for participation, delivery, and community self-governance.",
    kpiActiveIssues: "Active issues",
    kpiActiveContributors: "Active contributors",
    kpiTotalEngagements: "Total engagements",
    kpiPipelineHealth: "Pipeline health",
    kpiActiveTeabates: "Active teabates",
    kpiGovernanceScore: "Governance score",
    changePipelineTitle: "Change pipeline",
    changePipelineSubtitle:
      "How far issues move from analysis into strategy and execution.",
    pipelineStageIssues: "Issues",
    pipelineStageAnalysis: "Analysis",
    pipelineStageStrategy: "Strategy",
    pipelineStageExecution: "Execution",
    pipelineStageCovered: "Fully covered",
    pipelineOfTotal: "of all active issues",
    pipelineConversion: "conversion",
    activityTrendTitle: "Engagement trends",
    activityTrendSubtitle:
      "Track how community energy splits between contributions, analysis, and governance work over time.",
    activityLayerContributions: "Contributions",
    activityLayerAnalysis: "Analysis",
    activityLayerGovernance: "Governance",
    activityLayerTotal: "Total",
    priorityFocusTitle: "Priority focus",
    priorityFocusSubtitle:
      "A Pareto-style view of the issues drawing the most community energy right now.",
    priorityParetoSummary: "Top {count} issues account for {share}% of the ranked issue activity shown here.",
    priorityCoverageComplete: "Solution and action linked",
    priorityMissingSolution: "Needs a linked solution",
    priorityMissingAction: "Needs a linked action",
    priorityMissingBoth: "Needs both solution and action",
    governanceTitle: "Governance and community voice",
    governanceSubtitle:
      "Feedback, teabates, sentiment, and content health show how the community governs itself.",
    governanceScoreLabel: "Governance score",
    feedbackPipelineTitle: "Feedback pipeline",
    feedbackPipelineSubtitle: "How product feedback is moving through review and delivery.",
    feedbackOpen: "Open items",
    feedbackNewInWindow: "New this window",
    feedbackResponseRate: "Response rate",
    feedbackStatusLabels: {
      Open: "Open",
      Reviewing: "Reviewing",
      Planned: "Planned",
      Unplanned: "Unplanned",
      Developing: "Developing",
      Preview: "Preview",
      Released: "Released",
      Closed: "Closed",
    },
    teabateParticipationTitle: "Teabate participation",
    teabateParticipationSubtitle: "Teabates and alignment responses reflect structured civic deliberation.",
    teabateActive: "Active teabates",
    teabateNew: "New teabates",
    teabateAlignmentParticipants: "Alignment participants",
    communitySentimentTitle: "Community sentiment",
    communitySentimentSubtitle: "How many members recorded a stance on issues this period.",
    sentimentParticipants: "Participants",
    sentimentResponses: "Responses",
    sentimentPositive: "Positive",
    sentimentNeutral: "Neutral",
    sentimentNegative: "Negative",
    contentHealthTitle: "Content health",
    contentHealthSubtitle: "Moderation volume and turnaround at the community level.",
    reportsInWindow: "Reports this window",
    reportsPending: "Reports pending",
    reportsResolved: "Reports resolved",
    reportResolutionRate: "Resolution rate",
    alignmentTitle: "Organizational alignment",
    alignmentSubtitle:
      "Community alignment across the platform’s social dimensions and teabate axes.",
    alignmentSummary: "Overall",
    alignmentDimensionNone: "General",
    alignmentDimensionLabels: {
      nationalism_cosmopolitanism: "Diplomacy",
      theocracy_secularism: "Religion",
      authority_liberty: "State",
      capitalism_socialism: "Economics",
      dogma_science: "Beliefs",
      autocracy_democracy: "Government",
      conservatism_progressivism: "Traditions",
      content_discontent: "Sentiment",
      nationalism: "Nationalism",
      cosmopolitanism: "Cosmopolitanism",
      theocracy: "Theocracy",
      secularism: "Secularism",
      authority: "Authority",
      liberty: "Liberty",
      capitalism: "Capitalism",
      socialism: "Socialism",
      dogma: "Dogma",
      science: "Science",
      autocracy: "Autocracy",
      democracy: "Democracy",
      conservatism: "Conservatism",
      progressivism: "Progressivism",
      content: "Status Quo",
      discontent: "Reformation",
    },
    alignmentResponses: "Alignment responses",
    alignmentOverallScore: "Overall score",
    alignmentDimensionParticipation: "responses",
    radarCaption:
      "Relative scores (0–100) across the most active alignment dimensions in the selected window.",
    contributorsTitle: "Top contributors",
    contributorsSubtitle:
      "People putting the most visible work into discussion, delivery, and recognition loops.",
    contributorComments: "Comments",
    contributorPosts: "Posts",
    contributorAdBreaks: "Ad breaks",
    contributorAwards: "Awards",
    contributorScrollPrevious: "Scroll contributors left",
    contributorScrollNext: "Scroll contributors right",
    globalPresenceTitle: "Global presence",
    globalPresenceSubtitle:
      "A localized view of where public community participation is coming from.",
    globalPublicCountryUsers: "Public country profiles",
    demographicsNote: "Based on public profile locations with a country field.",
    countryUnknown: "Other / not set",
    openOpportunitiesTitle: "Open opportunities",
    openOpportunitiesSubtitle:
      "Where the enterprise still needs more strategy, execution, or governance participation.",
    opportunityStrategyTitle: "Strategy and execution gaps",
    opportunityGovernanceTitle: "Governance gaps",
    opportunityProposeSolution: "Propose solution",
    opportunityStartAction: "Start action",
    opportunityReviewFeedback: "Review feedback",
    opportunityJoinTeabates: "Join teabates",
    opportunityRecordSentiment: "Record sentiment",
    opportunityMissingSolution: "Missing linked solution",
    opportunityMissingAction: "Missing linked action",
    opportunityMissingBoth: "Missing linked solution and action",
    opportunityFeedbackQueue: "{count} feedback items are still open and need response.",
    opportunityTeabateParticipation: "{count} active teabates need more people to register their stance.",
    opportunitySentimentCoverage:
      "Issue sentiment participation is still light compared with the broader contributor base.",
    opportunityNoStrategyGaps: "The highest-activity issues all have at least one solution and one action linked.",
    opportunityNoGovernanceGaps: "Governance indicators are healthy for the current window.",
    emptyActivityTrend: "No community activity is available for this window yet.",
    emptyPriorityFocus: "No ranked issue activity is available for this window yet.",
    emptyContributors: "No contributor activity is available for this window yet.",
    emptyAlignment: "No alignment data is available for this window yet.",
    emptyDemographics: "No public country data is available yet.",
    emptyGovernance: "No governance activity is available for this window yet.",
  },
  "ja-JP": {
    navLabel: "ダッシュボード",
    pageTitle: "コミュニティダッシュボード",
    windowDays: "過去{days}日",
    windowDropdownLabel: "期間",
    tabDashboard: "ダッシュボード",
    tabPeople: "ピープル",
    tabAction: "アクション",
    peopleFilterLabel: "ピープルを絞り込む",
    peopleFilterEveryone: "すべて",
    peopleFilterUsers: "ユーザー",
    peopleFilterParteas: "パーティー",
    userSubfilterLabel: "ユーザーを絞り込む",
    userSubfilterAll: "すべて",
    userSubfilterFollowing: "フォロー中",
    userSubfilterFollowers: "フォロワー",
    parteaSubfilterLabel: "パーティーを絞り込む",
    parteaSubfilterAll: "すべて",
    parteaSubfilterMy: "自分のパーティー",
    followingEmptyTitle: "まだ誰もフォローしていません",
    followingEmptySubtitle: "フォローしているユーザーがここに表示されます。",
    followersEmptyTitle: "フォロワーはまだいません",
    followersEmptySubtitle: "他のユーザーにフォローされるとここに表示されます。",
    loading: "コミュニティダッシュボードを読み込み中…",
    sectionError: "このセクションを読み込めませんでした。",
    retry: "再試行",
    openIssue: "イシューを開く",
    openProfile: "プロフィールを開く",
    viewIssues: "イシューを見る",
    viewFeedback: "フィードバックを見る",
    viewTeabates: "ティーベイトを見る",
    viewSentiment: "イシューの感情を見る",
    deltaUp: "増加",
    deltaDown: "減少",
    deltaFlat: "変化なし",
    deltaFromPreviousWindow: "前期間比",
    deltaPoints: "pt",
    enterprisePulseTitle: "エンタープライズ指標",
    enterprisePulseSubtitle:
      "参加、実行、コミュニティの自己統治を示す主要指標です。",
    kpiActiveIssues: "有効なイシュー",
    kpiActiveContributors: "活動中の貢献者",
    kpiTotalEngagements: "総エンゲージメント",
    kpiPipelineHealth: "パイプライン健全度",
    kpiActiveTeabates: "進行中のティーベイト",
    kpiGovernanceScore: "ガバナンス健全度",
    changePipelineTitle: "変化のパイプライン",
    changePipelineSubtitle:
      "イシューが分析から戦略、実行へどこまで進んでいるかを示します。",
    pipelineStageIssues: "イシュー",
    pipelineStageAnalysis: "分析",
    pipelineStageStrategy: "戦略",
    pipelineStageExecution: "実行",
    pipelineStageCovered: "充足済み",
    pipelineOfTotal: "有効イシュー全体に対する割合",
    pipelineConversion: "転換率",
    activityTrendTitle: "参加トレンド",
    activityTrendSubtitle:
      "コミュニティのエネルギーが投稿、分析、ガバナンス作業にどう分かれているかを時系列で追跡します。",
    activityLayerContributions: "投稿",
    activityLayerAnalysis: "分析",
    activityLayerGovernance: "ガバナンス",
    activityLayerTotal: "合計",
    priorityFocusTitle: "重点領域",
    priorityFocusSubtitle:
      "現在もっともコミュニティのエネルギーを集めているイシューをパレート的に示します。",
    priorityParetoSummary: "上位{count}件のイシューで、このランキング活動の{share}%を占めています。",
    priorityCoverageComplete: "ソリューションとアクションが両方リンク済み",
    priorityMissingSolution: "リンクされたソリューションが必要です",
    priorityMissingAction: "リンクされたアクションが必要です",
    priorityMissingBoth: "ソリューションとアクションの両方が必要です",
    governanceTitle: "ガバナンスとコミュニティの声",
    governanceSubtitle:
      "フィードバック、議論、感情、コンテンツ健全性から、コミュニティが自らをどう統治しているかを見ます。",
    governanceScoreLabel: "ガバナンス健全度",
    feedbackPipelineTitle: "フィードバックパイプライン",
    feedbackPipelineSubtitle: "プロダクトフィードバックが確認と提供をどう進んでいるかを示します。",
    feedbackOpen: "未対応",
    feedbackNewInWindow: "この期間の新規",
    feedbackResponseRate: "応答率",
    feedbackStatusLabels: {
      Open: "未対応",
      Reviewing: "確認中",
      Planned: "予定",
      Unplanned: "予定なし",
      Developing: "開発中",
      Preview: "プレビュー",
      Released: "リリース済み",
      Closed: "完了",
    },
    teabateParticipationTitle: "ティーベイト参加",
    teabateParticipationSubtitle: "ティーベイトと位置付け回答は、構造化された熟議の状態を示します。",
    teabateActive: "進行中のティーベイト",
    teabateNew: "新しいティーベイト",
    teabateAlignmentParticipants: "位置付け参加者",
    communitySentimentTitle: "コミュニティ感情",
    communitySentimentSubtitle: "この期間にイシューへの立場を記録した人数です。",
    sentimentParticipants: "参加者",
    sentimentResponses: "回答数",
    sentimentPositive: "ポジティブ",
    sentimentNeutral: "中立",
    sentimentNegative: "ネガティブ",
    contentHealthTitle: "コンテンツ健全性",
    contentHealthSubtitle: "コミュニティ全体での通報量と対応速度です。",
    reportsInWindow: "この期間の通報",
    reportsPending: "保留中の通報",
    reportsResolved: "解決済み通報",
    reportResolutionRate: "解決率",
    alignmentTitle: "組織的な位置付け",
    alignmentSubtitle:
      "コミュニティの社会軸と議論軸における位置付けを可視化します。",
    alignmentSummary: "全体",
    alignmentDimensionNone: "一般",
    alignmentDimensionLabels: {
      nationalism_cosmopolitanism: "外交",
      theocracy_secularism: "宗教",
      authority_liberty: "国家",
      capitalism_socialism: "経済",
      dogma_science: "信念",
      autocracy_democracy: "統治",
      conservatism_progressivism: "伝統",
      content_discontent: "感情",
      nationalism: "国家主義",
      cosmopolitanism: "世界主義",
      theocracy: "神権主義",
      secularism: "世俗主義",
      authority: "権威",
      liberty: "自由",
      capitalism: "資本主義",
      socialism: "社会主義",
      dogma: "教義",
      science: "科学",
      autocracy: "専制",
      democracy: "民主主義",
      conservatism: "保守主義",
      progressivism: "進歩主義",
      content: "現状維持",
      discontent: "改革",
    },
    alignmentResponses: "位置付け回答数",
    alignmentOverallScore: "総合スコア",
    alignmentDimensionParticipation: "回答",
    radarCaption:
      "選択した期間でもっとも活発だった位置付け軸における相対スコア（0〜100）です。",
    contributorsTitle: "主要な貢献者",
    contributorsSubtitle:
      "議論、実行、評価の循環に最も見える形で関わっている人たちです。",
    contributorComments: "コメント",
    contributorPosts: "投稿",
    contributorAdBreaks: "広告ブレイク",
    contributorAwards: "アワード",
    contributorScrollPrevious: "貢献者を左へスクロール",
    contributorScrollNext: "貢献者を右へスクロール",
    globalPresenceTitle: "グローバルな広がり",
    globalPresenceSubtitle:
      "公開プロフィールに基づくコミュニティ参加の地域分布です。",
    globalPublicCountryUsers: "公開国プロフィール",
    demographicsNote: "公開プロフィールの国情報に基づいて集計します。",
    countryUnknown: "その他／未設定",
    openOpportunitiesTitle: "未着手の機会",
    openOpportunitiesSubtitle:
      "戦略、実行、またはガバナンス参加がまだ不足している領域です。",
    opportunityStrategyTitle: "戦略と実行のギャップ",
    opportunityGovernanceTitle: "ガバナンスのギャップ",
    opportunityProposeSolution: "ソリューションを提案",
    opportunityStartAction: "アクションを開始",
    opportunityReviewFeedback: "フィードバックを見る",
    opportunityJoinTeabates: "ティーベイトに参加",
    opportunityRecordSentiment: "感情を記録",
    opportunityMissingSolution: "リンクされたソリューションが不足",
    opportunityMissingAction: "リンクされたアクションが不足",
    opportunityMissingBoth: "ソリューションとアクションの両方が不足",
    opportunityFeedbackQueue: "未対応のフィードバックが{count}件あり、応答が必要です。",
    opportunityTeabateParticipation:
      "進行中のティーベイトが{count}件あり、さらに多くの立場表明が必要です。",
    opportunitySentimentCoverage:
      "イシュー感情の参加率は、より広い貢献者層に比べてまだ低い状態です。",
    opportunityNoStrategyGaps:
      "活動量の高いイシューには、すべて少なくとも一つのソリューションとアクションがリンクされています。",
    opportunityNoGovernanceGaps: "この期間のガバナンス指標は良好です。",
    emptyActivityTrend: "この期間に表示できるコミュニティ活動はまだありません。",
    emptyPriorityFocus: "この期間に表示できる優先イシューはまだありません。",
    emptyContributors: "この期間に表示できる貢献者活動はまだありません。",
    emptyAlignment: "この期間に位置付けデータはまだありません。",
    emptyDemographics: "公開できる国別データはまだありません。",
    emptyGovernance: "この期間にガバナンス活動はまだありません。",
  },
  "ko-KR": {
    navLabel: "대시보드",
    pageTitle: "커뮤니티 대시보드",
    windowDays: "최근 {days}일",
    windowDropdownLabel: "기간",
    tabDashboard: "대시보드",
    tabPeople: "피플",
    tabAction: "액션",
    peopleFilterLabel: "피플 필터",
    peopleFilterEveryone: "모두",
    peopleFilterUsers: "사용자",
    peopleFilterParteas: "파티",
    userSubfilterLabel: "사용자 필터",
    userSubfilterAll: "모두",
    userSubfilterFollowing: "팔로우",
    userSubfilterFollowers: "팔로워",
    parteaSubfilterLabel: "파티 필터",
    parteaSubfilterAll: "모두",
    parteaSubfilterMy: "내 파티",
    followingEmptyTitle: "아직 팔로우하는 사용자가 없습니다",
    followingEmptySubtitle: "팔로우하는 사용자가 여기에 표시됩니다.",
    followersEmptyTitle: "아직 팔로워가 없습니다",
    followersEmptySubtitle: "다른 사용자가 팔로우하면 여기에 표시됩니다.",
    loading: "커뮤니티 대시보드를 불러오는 중…",
    sectionError: "이 섹션을 불러올 수 없습니다.",
    retry: "다시 시도",
    openIssue: "이슈 열기",
    openProfile: "프로필 열기",
    viewIssues: "이슈 보기",
    viewFeedback: "피드백 보기",
    viewTeabates: "티베이트 보기",
    viewSentiment: "이슈 정서 보기",
    deltaUp: "상승",
    deltaDown: "하락",
    deltaFlat: "변화 없음",
    deltaFromPreviousWindow: "이전 기간 대비",
    deltaPoints: "pt",
    enterprisePulseTitle: "엔터프라이즈 펄스",
    enterprisePulseSubtitle:
      "참여, 실행, 커뮤니티 자기 통치를 보여 주는 핵심 지표입니다.",
    kpiActiveIssues: "활성 이슈",
    kpiActiveContributors: "활동 중인 기여자",
    kpiTotalEngagements: "총 참여 수",
    kpiPipelineHealth: "파이프라인 건강도",
    kpiActiveTeabates: "활성 티베이트",
    kpiGovernanceScore: "거버넌스 건강도",
    changePipelineTitle: "변화 파이프라인",
    changePipelineSubtitle:
      "이슈가 분석에서 전략과 실행으로 얼마나 전진했는지 보여 줍니다.",
    pipelineStageIssues: "이슈",
    pipelineStageAnalysis: "분석",
    pipelineStageStrategy: "전략",
    pipelineStageExecution: "실행",
    pipelineStageCovered: "완전 커버",
    pipelineOfTotal: "전체 활성 이슈 대비",
    pipelineConversion: "전환율",
    activityTrendTitle: "참여 추세",
    activityTrendSubtitle:
      "시간에 따라 커뮤니티 에너지가 기여, 분석, 거버넌스 작업으로 어떻게 나뉘는지 추적합니다.",
    activityLayerContributions: "기여",
    activityLayerAnalysis: "분석",
    activityLayerGovernance: "거버넌스",
    activityLayerTotal: "합계",
    priorityFocusTitle: "우선 집중 영역",
    priorityFocusSubtitle:
      "지금 가장 많은 커뮤니티 에너지를 끌어들이는 이슈를 파레토 관점으로 보여 줍니다.",
    priorityParetoSummary: "상위 {count}개 이슈가 표시된 활동의 {share}%를 차지합니다.",
    priorityCoverageComplete: "솔루션과 액션이 모두 연결됨",
    priorityMissingSolution: "연결된 솔루션이 필요함",
    priorityMissingAction: "연결된 액션이 필요함",
    priorityMissingBoth: "솔루션과 액션이 모두 필요함",
    governanceTitle: "거버넌스와 커뮤니티의 목소리",
    governanceSubtitle:
      "피드백, 토론, 정서, 콘텐츠 건강 상태를 통해 커뮤니티가 스스로를 어떻게 운영하는지 보여 줍니다.",
    governanceScoreLabel: "거버넌스 건강도",
    feedbackPipelineTitle: "피드백 파이프라인",
    feedbackPipelineSubtitle: "제품 피드백이 검토와 전달 단계에서 어떻게 이동하는지 보여 줍니다.",
    feedbackOpen: "열린 항목",
    feedbackNewInWindow: "이 기간 신규",
    feedbackResponseRate: "응답률",
    feedbackStatusLabels: {
      Open: "열림",
      Reviewing: "검토 중",
      Planned: "계획됨",
      Unplanned: "계획 없음",
      Developing: "개발 중",
      Preview: "미리 보기",
      Released: "출시됨",
      Closed: "닫힘",
    },
    teabateParticipationTitle: "티베이트 참여",
    teabateParticipationSubtitle: "티베이트와 정렬 응답은 구조화된 숙의의 상태를 보여 줍니다.",
    teabateActive: "활성 티베이트",
    teabateNew: "새 티베이트",
    teabateAlignmentParticipants: "정렬 참여자",
    communitySentimentTitle: "커뮤니티 정서",
    communitySentimentSubtitle: "이 기간에 이슈에 대한 입장을 기록한 사람 수입니다.",
    sentimentParticipants: "참여자",
    sentimentResponses: "응답 수",
    sentimentPositive: "긍정",
    sentimentNeutral: "중립",
    sentimentNegative: "부정",
    contentHealthTitle: "콘텐츠 건강도",
    contentHealthSubtitle: "커뮤니티 수준의 신고량과 처리 속도입니다.",
    reportsInWindow: "이 기간 신고",
    reportsPending: "대기 중 신고",
    reportsResolved: "해결된 신고",
    reportResolutionRate: "해결률",
    alignmentTitle: "조직적 정렬",
    alignmentSubtitle:
      "커뮤니티가 플랫폼의 사회적 축과 토론 축에서 어디에 위치하는지 보여 줍니다.",
    alignmentSummary: "전체",
    alignmentDimensionNone: "일반",
    alignmentDimensionLabels: {
      nationalism_cosmopolitanism: "외교",
      theocracy_secularism: "종교",
      authority_liberty: "국가",
      capitalism_socialism: "경제",
      dogma_science: "신념",
      autocracy_democracy: "정치 체제",
      conservatism_progressivism: "전통",
      content_discontent: "정서",
      nationalism: "민족주의",
      cosmopolitanism: "세계주의",
      theocracy: "신정주의",
      secularism: "세속주의",
      authority: "권위",
      liberty: "자유",
      capitalism: "자본주의",
      socialism: "사회주의",
      dogma: "교조주의",
      science: "과학",
      autocracy: "전제정",
      democracy: "민주주의",
      conservatism: "보수주의",
      progressivism: "진보주의",
      content: "현상 유지",
      discontent: "개혁",
    },
    alignmentResponses: "정렬 응답",
    alignmentOverallScore: "전체 점수",
    alignmentDimensionParticipation: "응답",
    radarCaption:
      "선택한 기간 동안 가장 활발했던 정렬 축의 상대 점수(0–100)입니다.",
    contributorsTitle: "상위 기여자",
    contributorsSubtitle:
      "토론, 실행, 보상 순환에 가장 뚜렷하게 기여한 사람들입니다.",
    contributorComments: "댓글",
    contributorPosts: "게시물",
    contributorAdBreaks: "광고 브레이크",
    contributorAwards: "어워드",
    contributorScrollPrevious: "기여자를 왼쪽으로 스크롤",
    contributorScrollNext: "기여자를 오른쪽으로 스크롤",
    globalPresenceTitle: "글로벌 분포",
    globalPresenceSubtitle:
      "공개 프로필 기준으로 커뮤니티 참여가 어디에서 오는지 보여 줍니다.",
    globalPublicCountryUsers: "공개 국가 프로필",
    demographicsNote: "국가 정보가 공개된 프로필 위치를 기준으로 집계합니다.",
    countryUnknown: "기타/미설정",
    openOpportunitiesTitle: "열린 기회",
    openOpportunitiesSubtitle:
      "전략, 실행 또는 거버넌스 참여가 더 필요한 영역입니다.",
    opportunityStrategyTitle: "전략 및 실행 격차",
    opportunityGovernanceTitle: "거버넌스 격차",
    opportunityProposeSolution: "솔루션 제안",
    opportunityStartAction: "액션 시작",
    opportunityReviewFeedback: "피드백 보기",
    opportunityJoinTeabates: "티베이트 참여",
    opportunityRecordSentiment: "정서 기록",
    opportunityMissingSolution: "연결된 솔루션이 없음",
    opportunityMissingAction: "연결된 액션이 없음",
    opportunityMissingBoth: "솔루션과 액션이 모두 없음",
    opportunityFeedbackQueue: "열린 피드백이 {count}건 있어 추가 응답이 필요합니다.",
    opportunityTeabateParticipation:
      "활성 티베이트 {count}건에 더 많은 입장 등록이 필요합니다.",
    opportunitySentimentCoverage:
      "이슈 정서 참여율은 전체 기여자 기반에 비해 아직 낮습니다.",
    opportunityNoStrategyGaps:
      "활동량이 높은 이슈는 모두 최소 하나의 솔루션과 액션이 연결되어 있습니다.",
    opportunityNoGovernanceGaps: "현재 기간의 거버넌스 지표는 건강합니다.",
    emptyActivityTrend: "이 기간에 표시할 커뮤니티 활동이 아직 없습니다.",
    emptyPriorityFocus: "이 기간에 표시할 우선 이슈가 아직 없습니다.",
    emptyContributors: "이 기간에 표시할 기여자 활동이 아직 없습니다.",
    emptyAlignment: "이 기간에 정렬 데이터가 아직 없습니다.",
    emptyDemographics: "공개 국가 데이터가 아직 없습니다.",
    emptyGovernance: "이 기간에 거버넌스 활동이 아직 없습니다.",
  },
};

function humanizeAlignmentKey(key: string): string {
  const trimmed = key.trim();
  if (!trimmed) {
    return "";
  }

  return trimmed
    .replace(/[_-]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function formatCommunityAlignmentLabel(key: string, strings: CommunityUiStrings): string {
  const normalized = key.trim().toLowerCase();

  if (!normalized || normalized === "none") {
    return strings.alignmentDimensionNone;
  }
  if (normalized === "summary") {
    return strings.alignmentSummary;
  }

  return strings.alignmentDimensionLabels[normalized] || humanizeAlignmentKey(key) || strings.alignmentDimensionNone;
}

export function formatCommunityFeedbackStatus(status: string, strings: CommunityUiStrings): string {
  const normalized = status.trim();
  if (!normalized) {
    return strings.feedbackStatusLabels.Open;
  }

  return strings.feedbackStatusLabels[normalized] || normalized;
}

export function resolveCommunityLocale(): CommunityLocale {
  if (typeof navigator === "undefined" || !navigator.language) {
    return "en-US";
  }
  const lang = navigator.language.toLowerCase();
  if (lang.startsWith("ja")) {
    return "ja-JP";
  }
  if (lang.startsWith("ko")) {
    return "ko-KR";
  }
  return "en-US";
}

export function getCommunityUiStrings(locale?: CommunityLocale): CommunityUiStrings {
  const key = locale ?? resolveCommunityLocale();
  return STRINGS[key] ?? STRINGS["en-US"];
}
